import React, { useEffect, useMemo, useState } from 'react';
import { FaBolt, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InventoryEquipment.css';
import '../styles/InventoryUsage.css';

const PROJECTS = `
  query UsageLogProjects {
    findAllProject(limit: 200) {
      id
      projectId
      name
    }
  }
`;

const USERS = `
  query UsageLogUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data { id firstName lastName jobTitle }
    }
  }
`;

const ITEMS = `
  query UsageLogItems {
    findAllItem {
      id
      name
      code
      perItemCost
      unit
    }
  }
`;

const CREATE = `
  mutation CreateUsage($inputs: [CreateResourceUsageInput!]!) {
    createResourceUsageLogs(inputs: $inputs) {
      id
      status
      variance
    }
  }
`;

function personName(p) {
  return `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
}

function jobCode(project, index) {
  const raw = String(project?.projectId || '').trim();
  if (raw) return raw;
  return `JOB-${String(index + 1).padStart(4, '0')}`;
}

function todayInput() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function emptyLine() {
  return { itemId: '', productName: '', qtyUsed: '', expectedQty: '', unitCost: 0 };
}

function varianceOf(line) {
  const used = Number(line.qtyUsed);
  const expected = Number(line.expectedQty);
  if (!Number.isFinite(used) || !Number.isFinite(expected)) return '';
  const v = used - expected;
  return v > 0 ? `+${v}` : String(v);
}

export default function LogUsageModal({
  onClose,
  onSaved,
  expectedByItem = {},
}) {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    projectId: '',
    employeeId: '',
    usageDate: todayInput(),
    notes: '',
  });
  const [lines, setLines] = useState([emptyLine()]);

  useEffect(() => {
    Promise.all([
      graphqlRequest(PROJECTS, { limit: 200 }).catch(() => ({})),
      graphqlRequest(USERS, { page: 1, limit: 200 }).catch(() => ({})),
      graphqlRequest(ITEMS).catch(() => ({})),
    ]).then(([projRes, userRes, itemRes]) => {
      setProjects(
        Array.isArray(projRes?.findAllProject) ? projRes.findAllProject : [],
      );
      setUsers(
        Array.isArray(userRes?.getAllUser?.data) ? userRes.getAllUser.data : [],
      );
      setItems(Array.isArray(itemRes?.findAllItem) ? itemRes.findAllItem : []);
    });
  }, []);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === form.projectId) || null,
    [projects, form.projectId],
  );
  const selectedEmployee = useMemo(
    () => users.find((u) => u.id === form.employeeId) || null,
    [users, form.employeeId],
  );
  const site = selectedProject?.name || '';
  const jobNumber = selectedProject
    ? jobCode(selectedProject, projects.indexOf(selectedProject))
    : '';

  const setLine = (index, patch) => {
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );
  };

  const onPickItem = (index, itemId) => {
    const item = items.find((it) => it.id === itemId);
    const expected =
      expectedByItem[`${form.projectId}:${itemId}`] ??
      expectedByItem[itemId] ??
      '';
    setLine(index, {
      itemId,
      productName: item?.name || '',
      unitCost: Number(item?.perItemCost) || 0,
      expectedQty: expected === '' ? '' : String(expected),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.projectId) {
      toast.error('Select a job');
      return;
    }
    if (!form.employeeId) {
      toast.error('Select an employee');
      return;
    }
    if (!form.usageDate) {
      toast.error('Choose a date');
      return;
    }
    const valid = lines.filter(
      (l) => String(l.productName || '').trim() && Number(l.qtyUsed) >= 0 && l.qtyUsed !== '',
    );
    if (!valid.length) {
      toast.error('Add at least one usage item with quantity');
      return;
    }
    setSaving(true);
    try {
      await graphqlRequest(CREATE, {
        inputs: valid.map((line) => {
          const input = {
            usageDate: `${form.usageDate}T12:00:00.000Z`,
            projectId: form.projectId,
            jobNumber,
            site,
            employeeId: form.employeeId,
            employeeName: personName(selectedEmployee),
            productName: String(line.productName).trim(),
            qtyUsed: Number(line.qtyUsed) || 0,
            expectedQty: Number(line.expectedQty) || 0,
            unitCost: Number(line.unitCost) || 0,
          };
          if (line.itemId) input.itemId = line.itemId;
          if (form.notes) input.notes = form.notes;
          return input;
        }),
      });
      toast.success('Usage log saved');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save usage log');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ieq-overlay" onClick={onClose}>
      <div
        className="ieq-modal ieq-modal--form iut-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ieq-modal-header">
          <h2>Log Resource Usage</h2>
          <button type="button" className="ieq-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </header>
        <form className="ieq-modal-body" onSubmit={submit}>
          <div className="ieq-grid">
            <label className="ieq-field">
              <span>
                Job <em>*</em>
              </span>
              <select
                required
                value={form.projectId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, projectId: e.target.value }))
                }
              >
                <option value="">Select job</option>
                {projects.map((p, i) => (
                  <option key={p.id} value={p.id}>
                    {jobCode(p, i)} — {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="ieq-field">
              <span>Site (Auto-filled)</span>
              <input value={site} readOnly disabled placeholder="Select a job" />
            </label>
            <label className="ieq-field">
              <span>
                Employee <em>*</em>
              </span>
              <select
                required
                value={form.employeeId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, employeeId: e.target.value }))
                }
              >
                <option value="">Select employee</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {personName(u)}
                    {u.jobTitle ? ` — ${u.jobTitle}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="ieq-field">
              <span>
                Date <em>*</em>
              </span>
              <input
                type="date"
                required
                value={form.usageDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, usageDate: e.target.value }))
                }
              />
            </label>
          </div>

          <section className="ieq-section">
            <h3 className="ieq-section-title">
              <FaBolt /> Usage Items
            </h3>
            {lines.map((line, index) => (
              <div className="iut-line" key={index}>
                <select
                  value={line.itemId}
                  onChange={(e) => onPickItem(index, e.target.value)}
                >
                  <option value="">Select item</option>
                  {items.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name}
                      {it.code ? ` (${it.code})` : ''}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Qty used"
                  value={line.qtyUsed}
                  onChange={(e) => setLine(index, { qtyUsed: e.target.value })}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Expected"
                  value={line.expectedQty}
                  onChange={(e) =>
                    setLine(index, { expectedQty: e.target.value })
                  }
                />
                <input readOnly value={varianceOf(line)} placeholder="Variance" />
                <button
                  type="button"
                  className="iut-trash"
                  title="Remove item"
                  disabled={lines.length === 1}
                  onClick={() =>
                    setLines((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="iut-add-line"
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              <FaPlus /> Add Another Item
            </button>
          </section>

          <label className="ieq-field ieq-field--full">
            <span>Notes</span>
            <textarea
              rows={3}
              placeholder="Any additional notes."
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>

          <div className="ieq-modal-footer">
            <button type="button" className="ieq-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="ieq-btn ieq-btn--primary"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Submit Usage Log'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
